import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, grantWarningsFromError } from '@dataroom/shared';
import type { RoomAdminRole } from '@dataroom/shared';
import {
  useBillingRecovery,
  useRoomAdmins,
  useSetRoomAdminRole,
  useTransitionRoomLifecycle,
} from '@/api/hooks';
import { ActivityAlertsCard } from '@/components/ActivityAlertsCard';
import { DocumentHandlingCard } from '@/components/DocumentHandlingCard';
import { FiscalCalendarCard } from '@/components/FiscalCalendarCard';
import { RoomAdminsCard } from '@/components/RoomAdminsCard';
import { RoomConfigCard } from '@/components/RoomConfigCard';
import { WeeklyDigestCard } from '@/components/WeeklyDigestCard';
import { plainError } from '@/lib/errors';
import { billingErrorMessage, planLimitOf } from '@/lib/plan';
import { useRoomContext } from './context';

/** PRD-0004 §3a — room settings: the admin role, status (archive / trash /
 * reactivate), trashed-room recovery, the room's admins (co-admins, ADR-0049),
 * the fiscal year (ADR-0040), the weekly digest (ADR-0047) and the admin's own
 * buyer activity alerts (ADR-0056), then a static note on how documents are
 * processed. */
export function RoomSettingsPage() {
  const { roomId: id, room } = useRoomContext();
  const setAdminRole = useSetRoomAdminRole(id);
  const [draftRole, setDraftRole] = useState<RoomAdminRole>('intermediary');
  // Seed the draft from the room's saved role once it loads (and after a save).
  const savedRole = room.adminRole ?? null;
  useEffect(() => {
    if (savedRole) setDraftRole(savedRole);
  }, [savedRole]);
  const configWarnings = grantWarningsFromError(setAdminRole.error);
  const transitionLifecycle = useTransitionRoomLifecycle(id);
  // Restoring a trashed room goes through the $500 recovery-fee checkout (redirects to Stripe).
  const recover = useBillingRecovery(id);
  // The room's role and status are the main admin's (or the firm owner's) call; a
  // co-admin sees a plain note instead of controls the API would refuse (ADR-0049).
  const roomAdmins = useRoomAdmins(id);
  const mainOrOwner = roomAdmins.data?.canManage !== false;

  return (
    <div className="max-w-3xl space-y-6">
      {mainOrOwner ? (
        <RoomConfigCard
          room={room}
          draftRole={draftRole}
          onDraftRoleChange={setDraftRole}
          pending={setAdminRole.isPending}
          error={plainError(setAdminRole.error)}
          warnings={configWarnings}
          onSave={() => setAdminRole.mutate({ adminRole: draftRole })}
          onProceed={() => setAdminRole.mutate({ adminRole: draftRole, acknowledgeWarnings: true })}
          onCancelWarnings={() => setAdminRole.reset()}
          onTransition={(to) => transitionLifecycle.mutate({ to })}
          transitionPending={transitionLifecycle.isPending}
          transitionError={plainError(transitionLifecycle.error)}
          transitionLimit={planLimitOf(transitionLifecycle.error)}
          onDismissTransitionLimit={() => transitionLifecycle.reset()}
          onRecover={() => recover.mutate()}
          recoverPending={recover.isPending}
          recoverError={billingErrorMessage(recover.error)}
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Room role and status</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Only the room&apos;s main admin or your firm&apos;s owner can change the room&apos;s
              role or archive, trash or restore it.
            </p>
          </CardContent>
        </Card>
      )}
      <RoomAdminsCard roomId={id} />
      <FiscalCalendarCard room={room} />
      <WeeklyDigestCard roomId={id} readOnly={room.lifecycleState !== 'active'} />
      <ActivityAlertsCard roomId={id} readOnly={room.lifecycleState !== 'active'} />
      <DocumentHandlingCard />
    </div>
  );
}
